/**
 * Advanced Analytics Dashboard
 * RFM Analysis, Customer Segmentation, and more
 * THIS IS WHAT MAKES QORDER BETTER THAN SUNDAY!
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { advancedAnalyticsApi } from '../lib/api/analytics';
import { RFMScore, CustomerSegmentation } from '../types/analytics';
import { MetricCard } from '../components/analytics/MetricCard';
import { CustomerSegmentChart } from '../components/analytics/CustomerSegmentChart';
import { RFMScoreTable } from '../components/analytics/RFMScoreTable';

export default function AdvancedAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [rfmScores, setRfmScores] = useState<RFMScore[]>([]);
  const [segmentation, setSegmentation] = useState<CustomerSegmentation | null>(null);
  const [dateRange, setDateRange] = useState<'30d' | '90d' | '1y' | 'all'>('90d');

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date | undefined;

      switch (dateRange) {
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '1y':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = undefined;
      }

      const params = startDate
        ? {
            startDate: startDate.toISOString().split('T')[0],
            endDate: now.toISOString().split('T')[0],
          }
        : undefined;

      const [rfm, seg] = await Promise.all([
        advancedAnalyticsApi.getRFMAnalysis(params),
        advancedAnalyticsApi.getCustomerSegmentation(params),
      ]);

      setRfmScores(rfm);
      setSegmentation(seg);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      alert('Errore nel caricamento dei dati analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">Caricamento analytics avanzati...</p>
        </div>
      </div>
    );
  }

  if (!segmentation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="text-center text-gray-500 dark:text-slate-400">
          <p>Nessun dato disponibile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl border-b border-gray-200 dark:border-slate-700/50 shadow-sm dark:shadow-xl dark:shadow-slate-950/50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:drop-shadow-[0_2px_10px_rgba(255,255,255,0.15)]">Analytics Avanzati</h1>
              <p className="text-gray-600 dark:text-slate-400 mt-1">RFM Analysis & Customer Segmentation</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
              >
                <option value="30d">Ultimi 30 giorni</option>
                <option value="90d">Ultimi 90 giorni</option>
                <option value="1y">Ultimo anno</option>
                <option value="all">Tutto il periodo</option>
              </select>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 shadow-lg dark:shadow-indigo-900/50 transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Aggiorna
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Key Metrics */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Metriche Chiave</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Clienti Totali"
              value={segmentation.totalCustomers}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
              color="indigo"
              subtitle={`${segmentation.newCustomers} nuovi, ${segmentation.returningCustomers} di ritorno`}
            />
            <MetricCard
              title="Clienti VIP"
              value={segmentation.vipCustomers.length}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
              }
              color="purple"
              subtitle="Top 10% spenders"
            />
            <MetricCard
              title="Retention Rate"
              value={`${segmentation.customerRetentionRate.toFixed(1)}%`}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              color="green"
              subtitle={`Churn rate: ${segmentation.churnRate.toFixed(1)}%`}
            />
            <MetricCard
              title="Average LTV"
              value={`€${segmentation.averageCustomerLifetimeValue.toFixed(2)}`}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              color="amber"
              subtitle={`${segmentation.averageOrdersPerCustomer.toFixed(1)} ordini medi`}
            />
          </div>
        </div>

        {/* Customer Segmentation */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-lg dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-6 transition-all duration-300"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Segmentazione Clienti</h2>
            <CustomerSegmentChart data={segmentation.customersBySegment} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-lg dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-6 transition-all duration-300"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Clienti a Rischio</h2>
            {segmentation.atRiskCustomers.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  <span className="font-semibold text-red-600 dark:text-red-400">{segmentation.atRiskCustomers.length}</span> clienti
                  non ordinano da tempo ma hanno uno storico positivo.
                </p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {segmentation.atRiskCustomers.slice(0, 10).map((customer) => (
                    <div
                      key={customer.customerId}
                      className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-700/50 dark:ring-1 dark:ring-red-500/20"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {customer.customerName || customer.customerEmail || 'Cliente'}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-slate-400">
                          {customer.totalOrders} ordini • €{customer.lifetimeValue.toFixed(2)} LTV
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">
                          {customer.daysSinceLastOrder} giorni
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">ultimo ordine</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="w-full py-2 px-4 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 shadow-lg dark:shadow-red-900/50 transition-all duration-200 text-sm font-medium">
                  Invia Campagna di Retention
                </button>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                Nessun cliente a rischio! 🎉
              </div>
            )}
          </motion.div>
        </div>

        {/* Top Customers Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-lg dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-6 transition-all duration-300"
        >
          <RFMScoreTable customers={segmentation.topCustomers} title="Top 20 Clienti per Valore" limit={20} />
        </motion.div>

        {/* VIP Customers */}
        {segmentation.vipCustomers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 dark:backdrop-blur-xl rounded-xl shadow-lg dark:shadow-xl dark:shadow-purple-950/50 border border-purple-200 dark:border-purple-700/50 dark:ring-1 dark:ring-purple-500/20 p-6 transition-all duration-300"
          >
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Clienti VIP</h2>
            </div>
            <RFMScoreTable customers={segmentation.vipCustomers} title="" limit={10} />
          </motion.div>
        )}
      </div>
    </div>
  );
}
